import { useEffect, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { useShallow } from 'zustand/react/shallow';

import { toastService } from '@thefarmersfront/kitchen-mobile';

import { useCodesStore, useContractInputStore } from '~/store';
import type { TypeCenterShiftHour, TypeCodeState, TypeContract } from '~/types';
import { type TypeErrorFormat, getUniqueValues, removeEmptyField } from '~/utils';

import {
  type TypeKurlyWorksWorkShiftFormattedResponse,
  useGetCenterCodes,
  useGetClusterCodes,
  useGetKurlyWorksWorkShift,
  useGetWorkShiftType,
} from '../api';

export const useContractWorkInfo = () => {
  const {
    centerOptions: initialCenterOptions,
    workPartOptions: initialWorkPartOptions,
    teamNameOptions: initialTeamNameOptions,
    kurlyWorksWorkShiftTypeOptions: initialKurlyWorksWorkShiftTypeOptions,
    kurlyWorksWorkShiftSet: initialKurlyWorksWorkShiftSet,
    availableContractTimeSet: initialAvailableContractTimeSet,
    kurlyCenterShiftOptions: initialKurlyCenterShiftOptions,
  } = useContractInputStore(
    useShallow((state) => ({
      centerOptions: state.optionList.centerOptions,
      workPartOptions: state.optionList.workPartOptions,
      teamNameOptions: state.optionList.teamNameOptions,
      kurlyWorksWorkShiftTypeOptions: state.optionList.kurlyWorksWorkShiftTypeOptions,
      kurlyWorksWorkShiftSet: state.optionList.kurlyWorksWorkShiftSet,
      availableContractTimeSet: state.optionList.availableContractTimeSet,
      kurlyCenterShiftOptions: state.optionList.kurlyCenterShiftOptions,
    }))
  );
  const { storedClusterCodes } = useCodesStore(
    useShallow((state) => ({
      storedClusterCodes: state.clusterCodes,
    }))
  );

  const [centerOptions, setCenterOptions] = useState<TypeCodeState[] | null>(initialCenterOptions);
  const [workPartOptions, setWorkPartOptions] = useState<TypeCodeState[] | null>(initialWorkPartOptions);
  const [teamNameOptions, setTeamNameOptions] = useState<TypeCodeState[] | null>(initialTeamNameOptions);
  const [kurlyWorksWorkShiftTypeOptions, setKurlyWorksWorkShiftTypeOptions] = useState<TypeCodeState[] | null>(
    initialKurlyWorksWorkShiftTypeOptions
  );

  /** 옵션 선택 바텀시트 config */
  const [bottomSheetConfig, setBottomSheetConfig] = useState<{
    open: boolean;
    type: null | 'cluster' | 'center' | 'workPart' | 'teamName' | 'centerShiftHourType';
  }>({
    open: false,
    type: null,
  });

  const kurlyWorksWorkShiftSet = useRef<{
    [key: string]: {
      masterContractSeq: number;
      masterContractVersionSeq: number;
    };
  } | null>(initialKurlyWorksWorkShiftSet);

  const availableContractTimeSet = useRef<{
    [key: string]: {
      availableContractStartTime: string;
      availableContractEndTime: string;
    };
  } | null>(initialAvailableContractTimeSet);

  const kurlyCenterShiftOptions = useRef<TypeCenterShiftHour[] | null>(initialKurlyCenterShiftOptions);

  const {
    control,
    getValues,
    formState: { isDirty },
  } = useFormContext();

  const cluster = useWatch({ control, name: 'cluster' });
  const center = useWatch({ control, name: 'center' });
  const workPart = useWatch({ control, name: 'workPart' });
  const teamName = useWatch({ control, name: 'teamName' });

  const hasClusterCodes = storedClusterCodes !== null && storedClusterCodes.length > 0;

  const requestGetClusterOptions = useGetClusterCodes();
  const requestGetCenterOptions = useGetCenterCodes();
  const requestGetWorkShiftType = useGetWorkShiftType();

  const requestGetKurlyWorksWorkShift = useGetKurlyWorksWorkShift();

  useEffect(() => {
    // 클러스터 코드가 변경되면 센터 옵션 조회
    if (cluster?.code) {
      getCenters();
    }
  }, [cluster?.code]);

  const getClusters = () => requestGetClusterOptions();
  const getCenters = () => {
    requestGetCenterOptions({
      params: {
        cluster: cluster?.code ?? '',
      },
      onSuccess: (result) => setCenterOptions(result ?? []),
    });
  };

  // 센터코드 변경 시, 업무파트, 팀 옵션 조회
  const getWorkPartAndTeamNameOptions = () => {
    const params = removeEmptyField({
      cluster: getValues('cluster')?.code ?? '',
      center: getValues('center')?.code ?? '',
      teamName: getValues('teamName')?.code ?? '',
      workPart: getValues('workPart')?.code ?? '',
      contractType: 'SHORT' as TypeContract,
    });

    if (!params.teamName) {
      delete params.workPart;
    }

    return requestGetWorkShiftType({
      params,
      onSuccess: (response) => {
        const result = response?.data;

        if (!Array.isArray(result)) {
          kurlyCenterShiftOptions.current = [];
          setWorkPartOptions([]);
          setTeamNameOptions([]);
          setKurlyWorksWorkShiftTypeOptions([]);
          return;
        }

        const formattedResult = result?.map(({ active, teamName, workPart, workShift }) => ({
          workShift: workShift.formattedText,
          teamName,
          workPart,
          isActive: active,
        }));

        kurlyCenterShiftOptions.current = formattedResult;

        const workPartOptions = deleteDuplicatedOptions(formattedResult, 'workPart');
        const teamNameOptions = deleteDuplicatedOptions(formattedResult, 'teamName');
        setWorkPartOptions(workPartOptions);
        setTeamNameOptions(teamNameOptions);

        if (isDirty) {
          setKurlyWorksWorkShiftTypeOptions([]);
        }

        return workPartOptions;
      },
      onError: (error: TypeErrorFormat) => {
        toastService.addToast({ message: error.message });
        kurlyCenterShiftOptions.current = [];
        setWorkPartOptions([]);
        setTeamNameOptions([]);
        setKurlyWorksWorkShiftTypeOptions([]);
      },
    });
  };

  // 업무파트 코드 변경 시, 팀 옵션 조회
  const getFilteredTeamNameOptions = (changedWorkPart: string) => {
    const filteredOptionsByWorkPart =
      kurlyCenterShiftOptions.current?.filter(
        (option: TypeCenterShiftHour) => option.workPart.code === changedWorkPart
      ) ?? [];
    const teamNameOptions = deleteDuplicatedOptions(filteredOptionsByWorkPart, 'teamName');

    /** 페이지 진입 시 저장된 입력 정보가 있고, 팀 코드가 변경되지 않았다면 팀 코드 리셋시키지 않음 */
    if (isDirty) {
      setKurlyWorksWorkShiftTypeOptions([]);
    }

    setTeamNameOptions(teamNameOptions);
    return teamNameOptions;
  };

  // 팀 코드 변경 시, 근무조 옵션 조회
  const getCenterShiftHourOptions = async (changedTeamName: string) => {
    return requestGetKurlyWorksWorkShift({
      params: {
        cluster: getValues('cluster')?.code ?? '',
        workPart: getValues('workPart')?.code ?? '',
        center: getValues('center')?.code ?? '',
        teamName: changedTeamName,
      },
      onSuccess: (response) => {
        const { optionList, contractSeq, availableContractTime } =
          response as unknown as TypeKurlyWorksWorkShiftFormattedResponse;
        setKurlyWorksWorkShiftTypeOptions(optionList);
        kurlyWorksWorkShiftSet.current = contractSeq;
        availableContractTimeSet.current = availableContractTime;

        return optionList;
      },
      onError: (error: TypeErrorFormat) => {
        toastService.addToast({ message: error.message });
        setKurlyWorksWorkShiftTypeOptions([]);
        kurlyWorksWorkShiftSet.current = null;
        availableContractTimeSet.current = null;
      },
    });
  };

  // 중복 옵션 제거
  const deleteDuplicatedOptions = (
    options: TypeCenterShiftHour[],
    field: keyof Pick<TypeCenterShiftHour, 'cluster' | 'center' | 'workPart' | 'teamName'>
  ) => {
    const values = getUniqueValues<TypeCenterShiftHour>(options, field);
    const newOptions = values.map((value) => {
      if (typeof value === 'object' && 'code' in value) {
        return value;
      }
      return { code: String(value), text: String(value) };
    });

    return newOptions.length > 0 ? newOptions : [];
  };

  const openBottomSheet = (type: 'cluster' | 'center' | 'workPart' | 'teamName' | 'centerShiftHourType') => {
    setBottomSheetConfig({ open: true, type });
  };

  const closeBottomSheet = () => {
    setBottomSheetConfig({ open: false, type: null });
  };

  return {
    options: {
      storedClusterCodes,
      centerOptions,
      workPartOptions,
      teamNameOptions,
      kurlyWorksWorkShiftTypeOptions,
      kurlyWorksWorkShiftSet,
      availableContractTimeSet,
      kurlyCenterShiftOptions,
    },
    trigger: {
      getClusters,
      getCenters,
      getWorkPartAndTeamNameOptions,
      getFilteredTeamNameOptions,
      getCenterShiftHourOptions,
    },
    disabled: {
      cluster: hasClusterCodes,
      center: !cluster?.code || centerOptions?.length === 0,
      workPart: !center?.code || workPartOptions?.length === 0,
      teamName: !workPart?.code || teamNameOptions?.length === 0,
      centerShiftHourType: !teamName?.code || kurlyWorksWorkShiftTypeOptions?.length === 0,
    },
    bottomSheet: {
      bottomSheetConfig,
      openBottomSheet,
      closeBottomSheet,
    },
  };
};
